import React, { useEffect, useState } from 'react';
import { TermsDocument, Category } from 'common/types';
import { Prompt, Link as NavLink, useParams } from 'react-router-dom';
import {
	Typography,
	TextField,
	FormControlLabel,
	Checkbox,
	FormHelperText,
	Link,
} from '@mui/material';
import Container from 'components/Container';
import BookingBufferSelector from 'components/BookingBufferSelector';

import ContainerBox from 'components/ContainerBox';
import SaveButton from 'common/components/SaveButton';
import LoadingState from 'common/components/states/LoadingState';

import SetPageTitle from 'components/Portals/SetPageTitle';
import SetPageNavAction from 'components/Portals/SetPageNavAction';
import ProductGroup from 'components/ProductGroup';
import Banner from 'components/Banner';

import { Routes, push } from 'routing';
import HasFeature from 'components/features/HasFeature';
import { useTranslation, Trans } from 'services/localization';
import { isLiftTicketCategory } from 'common/utils/liftTicketUtils';

import {
	CATEGORY_MAX_DESCRIPTION_CHARS,
	LIFT_TICKET_CATEGORY_MAX_DESCRIPTION_CHARS,
} from 'common/constants/categories';
import { Grid } from '@mui/material';

import ImageUpload from 'components/ImageUpload';
import FileUploader from 'components/FileUploader';

import { useSelector, useDispatch } from 'react-redux';
import * as NotificationActions from 'actions/NotificationActions';
import * as StockSelectors from 'selectors/StockSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import { api } from 'common/frontend/api';
import { RiLock2Line } from 'react-icons/ri';
import {
	storageRef,
	deleteObject,
	uploadBytes,
	getDownloadURL,
} from 'common/frontend/firebase/storage';
import { FieldValues } from 'common/frontend/firebase/firestore';
import AddOnPromoBlock from 'components/features/promos/AddOnPromoBlock';

const CategoryView = () => {
	const { t } = useTranslation();

	const dispatch = useDispatch();
	const [loading, setLoading] = useState<boolean>(false);
	const [termsLoading, setTermsLoading] = useState<boolean>(false);
	const [savedChanges, setSavedChanges] = useState<boolean>(false);
	const { categoryId } = useParams<{ categoryId: string }>();

	const shopPublicInfo = useSelector(ShopSelectors.activeShopPublicInfo)!;
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const categories = useSelector(StockSelectors.categoriesData);
	const isAdmin = useSelector(UserSelectors.isAdmin);
	const productsByCategory = useSelector(StockSelectors.stockProductsByCategory);
	const categoryProducts = productsByCategory[categoryId] ?? [];
	const isEditingDisabled = !isAdmin;
	const _category = categories.find((c) => c.id === categoryId);

	const [category, setCategory] = useState<Category | undefined>(_category);
	const external = Boolean(category && category.external);
	const descriptionMaxChars =
		!!category && isLiftTicketCategory(category)
			? LIFT_TICKET_CATEGORY_MAX_DESCRIPTION_CHARS
			: CATEGORY_MAX_DESCRIPTION_CHARS;
	const descriptionCharsLeft = !!category?.description
		? descriptionMaxChars - category.description.def.length
		: descriptionMaxChars;

	const isNameValid = !!category && category.name.def.length > 0;
	const isDescriptionValid = descriptionCharsLeft >= 0;
	const isFormValid = isNameValid && isDescriptionValid;

	useEffect(() => {
		if (!!_category && !category) {
			setCategory(_category);
		}
	}, [category, _category]);

	const handleCategoryDescriptionChange = (value: string) => {
		const defLang = shopPublicInfo.defaultLanguage;

		setCategory({
			...category!,
			description: {
				...category!.description,
				def: value,
				[defLang]: value,
			},
		});
		setSavedChanges(true);
	};

	const handleCategoryNameChange = (value: string) => {
		const defLang = shopPublicInfo.defaultLanguage;
		setCategory({
			...category!,
			name: {
				...category!.name,
				def: value,
				[defLang]: value,
			},
		});
		setSavedChanges(true);
	};

	const handleImageChange = (image: string | null) => {
		const imageChanged = category ? (category.imageUrl || null) !== image : !!image;

		if (imageChanged) {
			setCategory({
				...category!,
				imageUrl: image ? image : '',
			});
			setSavedChanges(true);
		}
	};

	const toggleNoSegments = (event: React.ChangeEvent<HTMLInputElement>) => {
		setCategory({
			...category!,
			noSegments: event.target.checked,
		});
		setSavedChanges(true);
	};

	const handleCategoryDelete = () => {
		if (!category) return;
		if (
			window.confirm(
				t('products.deleteCategoryQuestion', 'Are you sure you want to delete the category?'),
			)
		) {
			api().productCategories.doc(category.id).delete();

			push(Routes.CatalogTab('categories'));
		}
	};

	const handleCategorySave = async () => {
		setLoading(true);
		try {
			await api()
				.productCategories.doc(categoryId)
				.set(
					{
						...category,
					},
					{
						merge: true,
					},
				);
			setSavedChanges(false);
		} catch (e) {
			dispatch(
				NotificationActions.showNotification({
					message: e.message,
					variant: 'error',
				}),
			);
		}
		setLoading(false);
	};

	const handleTermsUpload = async (file: File) => {
		const fileName = file.name.replace(/\.[^/.]+$/, '');
		const categoryId = category!.id;

		setTermsLoading(true);

		try {
			const fileRef = storageRef('terms-of-services/' + categoryId + '/' + fileName);
			const fileUpload = await uploadBytes(fileRef, file);
			const fileUrl = await getDownloadURL(fileUpload.ref);

			const terms: TermsDocument = {
				name: fileName,
				url: fileUrl,
				verified: false,
			};

			await api().productCategories.doc(categoryId).update({
				terms,
			});

			setCategory({
				...category!,
				terms,
			});
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		}

		setTermsLoading(false);
	};

	const handleTermsRemove = async () => {
		setTermsLoading(true);
		const categoryId = category!.id;
		const fileName = category!.terms?.name;

		try {
			if (fileName) {
				const fileRef = storageRef('terms-of-services/' + categoryId + '/' + fileName);
				deleteObject(fileRef);
			}
			await api().productCategories.doc(categoryId).update({
				terms: FieldValues.delete(),
			});
			setCategory({
				...category!,
				terms: null,
			});
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		}

		setTermsLoading(false);
	};

	if (!category) {
		return <LoadingState />;
	}

	return (
		<>
			<SetPageNavAction
				priority={2}
				backAction={{
					title: t('common:marketing.categories', 'Categories'),
					to: Routes.CatalogTab('categories'),
				}}
			/>
			<SetPageTitle
				priority={2}
				title={`${_category?.name.def}`}
				rightContent={
					<SaveButton
						disabled={isEditingDisabled || !savedChanges || !isFormValid}
						loading={loading}
						onClick={handleCategorySave}
						t={t}
					/>
				}
				contextActions={[
					{
						label: t('product.deleteCategory', 'Delete category'),
						onClick: handleCategoryDelete,
						disabled: isEditingDisabled || isLiftTicketCategory(category),
					},
				]}
			/>
			<Container>
				{isEditingDisabled && (
					<Banner
						variant="info"
						icon={RiLock2Line}
						title={t('access.editingDisabled', 'Editing is disabled')}
						description={t(
							'access.sharedCategoryDescription',
							'This is a shared category, which is only editable by admin users.',
						)}
					/>
				)}
				<ContainerBox>
					<Grid container spacing={2} justifyContent="space-between" alignItems="flex-start">
						<Grid item container spacing={2} xs={12} md={8}>
							<Grid item xs={12}>
								<Typography variant="h6">{t('product.basicInfo')}</Typography>
							</Grid>
							<Grid item xs={12}>
								<TextField
									id="name"
									variant="standard"
									label={t('common:form.name')}
									name="name"
									fullWidth
									value={category.name.def || ''}
									onChange={(e) => handleCategoryNameChange(e.target.value)}
									margin="normal"
									type="text"
									helperText={
										category.name.def.length === 0
											? t('common:validation.cannotBeEmptyGeneric')
											: ''
									}
									error={!isNameValid}
									disabled={isEditingDisabled}
								/>
							</Grid>
							<Grid item xs={12}>
								<TextField
									id="description"
									name="description"
									label={t('common:form.description')}
									placeholder={t('product.descriptionInfo')}
									multiline
									fullWidth
									maxRows="4"
									rows={4}
									variant="outlined"
									value={category.description ? category.description.def : ''}
									onChange={(e) => handleCategoryDescriptionChange(e.target.value)}
									margin="normal"
									aria-describedby="description-helperText"
									helperText={t('product.charactersLeft', {
										descriptionCharacters: descriptionCharsLeft,
										defaultValue: 'Characters left: {{descriptionCharacters}}.',
									})}
									error={!isDescriptionValid}
									disabled={isEditingDisabled}
								/>
							</Grid>
						</Grid>
						<Grid item container xs={12} sm={6} md={3} spacing={2}>
							<Grid item xs={12}>
								<ImageUpload
									value={category.imageUrl ?? undefined}
									onChange={(image) => {
										const imgUrl = !image ? null : Array.isArray(image) ? image[0] : image;
										handleImageChange(imgUrl);
									}}
									label={t('product.image', 'Image')}
									shopId={shopId}
									disabled={isEditingDisabled}
								/>
							</Grid>
							<Grid item xs={12} />
						</Grid>
					</Grid>
				</ContainerBox>
				<ContainerBox>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Typography variant="h6">
								{t('product.onlineBookingBuffer', 'Online booking buffer')}
							</Typography>
						</Grid>
						<Grid item xs={12}>
							<Typography variant="body1" gutterBottom>
								{t('category.header')}:
							</Typography>
							<BookingBufferSelector
								value={category.bookingBuffer}
								disabled={isEditingDisabled || external}
								onChange={(bookingBuffer) => {
									setCategory({
										...category,
										bookingBuffer,
									});
									setSavedChanges(true);
								}}
							/>
						</Grid>
					</Grid>
				</ContainerBox>
				<HasFeature requiredFeature={['SEGMENTS', 'CHECK_IN']}>
					<ContainerBox>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Typography variant="h6" gutterBottom>
									{t('shop.checkIn', 'Check-In')}
								</Typography>
							</Grid>
						</Grid>
						<Grid item xs={12}>
							<FormControlLabel
								style={{ alignItems: 'center' }}
								control={
									<Checkbox
										color="primary"
										checked={!!shopPublicInfo.noSegments ? true : !!category.noSegments}
										onChange={toggleNoSegments}
										value="noSegments"
										disabled={isEditingDisabled || !!shopPublicInfo.noSegments}
									/>
								}
								label={t(
									'shop.doNotAskSegmentInSelfService',
									'Do not ask for segment in check-in store',
								)}
							/>
							{!!shopPublicInfo.noSegments && (
								<FormHelperText>
									<Trans
										i18nKey="products.segmentsDisabledInShop"
										defaults="Self-service segments have been disabled for the shop. Enable them first in <channel>Channel settings</channel>"
										components={{
											channel: (
												<Link
													color="primary"
													component={({ children, className }) => (
														<NavLink className={className} to={Routes.StoreTab('check-in')}>
															{' '}
															{children}
														</NavLink>
													)}
												>
													{'Channel settings'}
												</Link>
											),
										}}
									/>
								</FormHelperText>
							)}
						</Grid>
					</ContainerBox>
				</HasFeature>
				<HasFeature
					requiredFeature="CATEGORY_TERMS"
					fallback={(failedChecks) => {
						return !failedChecks.user ? <AddOnPromoBlock addOn="CATEGORY_TERMS" /> : null;
					}}
				>
					<ContainerBox>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Typography variant="h6">{t('common:terms.termsUppercase')}</Typography>
								<Typography variant="body1" gutterBottom>
									{t('product.termsOfUseForCategory', 'The terms of use for the category')}
								</Typography>
							</Grid>
							<Grid item xs={12}>
								<FileUploader
									file={category.terms ?? undefined}
									onFileSelected={handleTermsUpload}
									onFileRemoved={handleTermsRemove}
									onError={(err) => {
										dispatch(
											NotificationActions.showNotification({ message: err, variant: 'error' }),
										);
									}}
									isUploading={termsLoading}
									disabled={isEditingDisabled}
								/>
							</Grid>
						</Grid>
					</ContainerBox>
				</HasFeature>

				{categoryProducts.length > 0 && (
					<ProductGroup
						label={t('common:products.products')}
						products={categoryProducts}
						enableReorder
					/>
				)}

				<Prompt when={savedChanges} message={() => t('common:prompts.unsavedChanges')} />
			</Container>
		</>
	);
};

export default CategoryView;
